/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { Button as ButtonVKUI, ButtonGroup, Card, Footnote, Link, Spacing, Subhead, Text, Title } from '@vkontakte/vkui';
import classNames from 'clsx';
import React, { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { EnvironmentSelectors } from 'reactApp/modules/environment/environment';
import { widgetTitles } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.constants';
import { FastCleanSelectors } from 'reactApp/modules/userQuotaCleaner/userQuotaCleaner.selectors';
import { QuotaCleanType } from 'reactApp/types/QuotaCleaner';
import { Dialog } from 'reactApp/ui/Dialog/Dialog';
import { MobileDialog } from 'reactApp/ui/Mobile/MobileDialog/MobileDialog';
import { Size } from 'reactApp/ui/Size/Size';

import { useSendFastCleanRadar } from '../useSendFastCleanRadar';
import { FastCleanerGroupTitles, FastCleanerIcon } from './FastCleanerComponents';
import styles from './FastCleanerModal.css';
import type { FastCleanerModalProps } from './FastCleanerModal.types';
import { useFastCleanerModalContent } from './useFastCleanerModalContent';

const MAX_DIGITS_COUNT = 3;

const FastCleanerGroupInfo = ({ group, last = false }) => {
    const { groupId, size_uniq } = group;
    if (!groupId) {
        return <></>;
    }

    return (
        <div className={styles.row}>
            <div className={styles.textWithIcon}>
                <FastCleanerIcon groupId={groupId} />
                <Subhead className={styles.subheadFont}>{FastCleanerGroupTitles[groupId] ?? widgetTitles[groupId]}</Subhead>
            </div>
            <Footnote className={classNames(styles.subheadFont, styles.secondaryText, { [styles.flex]: last })}>
                {last ? '~' : ''}
                <Size size={size_uniq} maxDigitsCount={MAX_DIGITS_COUNT} />
            </Footnote>
        </div>
    );
};

// Модалка умеет закрываться сама по кликам на кнопки
export const FastCleanerModal = memo<FastCleanerModalProps>((props) => {
    const { onPrimaryClick, onLinkClick, onCancel, onClose, closeOnPrimaryClick, closeOnLinkClick } = props;

    const isMobile = useSelector(EnvironmentSelectors.isMobile);
    const { messagesRemove, messagesSafe, messagesRemoveSize, messagesSafeSize } = useSelector(FastCleanSelectors.getMessages);
    const { cloudRemove, cloudSafe, cloudRemoveSize, cloudSafeSize } = useSelector(FastCleanSelectors.getCloud);

    const hasSafe = useMemo(() => cloudSafeSize + messagesSafeSize > 0, [cloudSafeSize, messagesSafeSize]);
    const hasRemove = useMemo(() => messagesRemoveSize + cloudRemoveSize > 0, [messagesRemoveSize, cloudRemoveSize]);
    const cleanType =
        hasRemove && hasSafe
            ? QuotaCleanType.full
            : hasRemove
            ? QuotaCleanType.remove
            : hasSafe
            ? QuotaCleanType.safe
            : QuotaCleanType.none;

    const sendRadar = useSendFastCleanRadar({ cleanType, category: 'modal', name: 'fast_clean_menu' });

    const { title, description, removeFootnote, safeFootnote } = useFastCleanerModalContent();

    useEffect(() => {
        sendRadar('show');
    }, []);

    const handlePrimaryButtonClick = useCallback(() => {
        sendRadar('click', { button_name: 'delete' });

        onPrimaryClick?.();
        if (closeOnPrimaryClick) {
            onClose?.();
        }
    }, []);

    const handleSecondaryClick = useCallback(() => {
        sendRadar('click', { button_name: 'leave' });

        onCancel?.();
        onClose?.();
    }, []);

    const handleLinkClick = useCallback(() => {
        sendRadar('click-info', { button_name: 'safe-description' });

        onLinkClick?.();
        if (closeOnLinkClick) {
            onClose?.();
        }
    }, []);

    const handleCancel = useCallback(() => {
        sendRadar('cancel');

        onCancel?.();
        onClose?.();
    }, []);

    const safeMessagesCardRef = useRef<HTMLDivElement>(null);
    const safeCloudCardRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (safeMessagesCardRef.current) {
            safeMessagesCardRef.current.scrollTop = 22;
        }
        if (safeCloudCardRef.current) {
            safeCloudCardRef.current.scrollTop = 22;
        }
    }, []);

    const groupsToRemove = (
        <>
            {/* Почта */}
            {messagesRemoveSize !== 0 && (
                <Card mode="tint" className={styles.card}>
                    <div className={styles.product}>
                        <FastCleanerGroupInfo group={{ groupId: 'mail', size_uniq: messagesRemoveSize }} />
                    </div>
                    {messagesRemove.map((group, index) => (
                        <FastCleanerGroupInfo key={group.groupId} group={group} last={index === messagesRemove.length - 1 && !hasSafe} />
                    ))}
                </Card>
            )}
            {/* Облако */}
            {cloudRemoveSize !== 0 && (
                <Card mode="tint" className={styles.card}>
                    <div className={styles.product}>
                        <FastCleanerGroupInfo group={{ groupId: 'cloud', size_uniq: cloudRemoveSize }} />
                    </div>
                    {cloudRemove.map((group, index) => (
                        <FastCleanerGroupInfo key={group.groupId} group={group} last={index === cloudRemove.length - 1 && !hasSafe} />
                    ))}
                </Card>
            )}
        </>
    );

    const groupsToSafe = (
        <>
            {/* Почта */}
            {messagesSafeSize !== 0 && (
                <Card mode="tint" className={styles.card}>
                    {!hasRemove && (
                        <div className={styles.product}>
                            <FastCleanerGroupInfo group={{ groupId: 'mail', size_uniq: messagesSafeSize }} />
                        </div>
                    )}
                    <div ref={safeMessagesCardRef} className={styles.groupList}>
                        {messagesSafe.map((group, index) => (
                            <FastCleanerGroupInfo key={group.groupId} group={group} last={index === messagesSafe.length - 1} />
                        ))}
                    </div>
                </Card>
            )}
            {/* Облако */}
            {cloudSafeSize !== 0 && (
                <Card mode="tint" className={styles.card}>
                    {!hasRemove && (
                        <div className={styles.product}>
                            <FastCleanerGroupInfo group={{ groupId: 'cloud', size_uniq: cloudSafeSize }} />
                        </div>
                    )}
                    <div ref={safeCloudCardRef} className={styles.groupList}>
                        {cloudSafe.map((group, index) => (
                            <FastCleanerGroupInfo key={group.groupId} group={group} last={index === cloudSafe.length - 1} />
                        ))}
                    </div>
                </Card>
            )}
        </>
    );

    const footer = (
        <>
            {safeFootnote && (
                <>
                    <Spacing size={12} />
                    <Footnote className={styles.secondaryText}>
                        {safeFootnote}
                        {'. '}
                        <Link onClick={handleLinkClick} hasVisited={false} data-test-id={'fastcleaner-modal-safelink'}>
                            Узнать больше о&nbsp;сейфе
                        </Link>
                    </Footnote>
                </>
            )}
            <Spacing size={24} />
            <ButtonGroup className={styles.buttons} mode={isMobile ? 'vertical' : 'horizontal'}>
                <ButtonVKUI
                    mode="primary"
                    appearance="accent"
                    size="l"
                    className={styles.primaryButton}
                    onClick={handlePrimaryButtonClick}
                    data-test-id={'fastcleaner-modal-primary'}
                >
                    {hasSafe ? 'Освободить место' : 'Удалить'}
                </ButtonVKUI>
                <ButtonVKUI
                    mode="primary"
                    appearance="neutral"
                    size="l"
                    onClick={handleSecondaryClick}
                    data-test-id={'fastcleaner-modal-secondary'}
                >
                    {hasSafe ? <>Оставить всё&nbsp;как&nbsp;есть</> : 'Не удалять'}
                </ButtonVKUI>
            </ButtonGroup>
        </>
    );

    if (isMobile) {
        return (
            <MobileDialog
                id="fastcleaner-modal-mobile"
                className={styles.mobileRoot}
                closeOnDimmerClick
                onClose={handleCancel}
                mod="confirm"
                topmost
                isRebrandingQuotaLanding
            >
                <div>
                    <Title level="2">{title}</Title>
                    <Spacing size={15} />
                    <Text className={styles.descriptionText}>{description}</Text>
                    <Spacing size={24} />
                </div>
                <div className={styles.contentWrapper}>
                    {/* Удаление... */}
                    {/* eslint-disable-next-line sonarjs/no-duplicate-string */}
                    <div className={styles['groupGrid-1c']}>{groupsToRemove}</div>
                    {removeFootnote && (
                        <>
                            <Spacing size={12} />
                            <Footnote className={styles.secondaryText}>{removeFootnote}</Footnote>
                            <Spacing size={32} />
                        </>
                    )}
                    {/* ...и сейфование */}
                    <div className={styles['groupGrid-1c']}>{groupsToSafe}</div>
                    {footer}
                </div>
            </MobileDialog>
        );
    }

    return (
        <Dialog
            open
            id="fastcleaner-modal"
            className={styles.root}
            closable
            size="large"
            bigCloseIcon
            qaId={'fastcleaner-modal'}
            onCancel={handleCancel}
        >
            <div>
                <Spacing size={24} />
                <Title>{title}</Title>
                <Spacing size={12} />
                <Text className={styles.descriptionText}>{description}</Text>
                <Spacing size={24} />
            </div>
            {/* Удаление... */}
            <div className={messagesRemoveSize > 0 && cloudRemoveSize > 0 ? styles['groupGrid-2c'] : styles['groupGrid-1c']}>
                {groupsToRemove}
            </div>
            {removeFootnote && (
                <>
                    <Spacing size={12} />
                    <Footnote className={styles.secondaryText}>{removeFootnote}</Footnote>
                    <Spacing size={20} />
                </>
            )}
            {/* ...и сейфование */}
            <div className={messagesSafeSize > 0 && cloudSafeSize > 0 ? styles['groupGrid-2c'] : styles['groupGrid-1c']}>
                {groupsToSafe}
            </div>
            {footer}
            <Spacing size={32} />
        </Dialog>
    );
});

FastCleanerModal.displayName = 'FastCleanerModal';

export default FastCleanerModal;
